import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import api, { thumbSrc } from "../api";
import { brand, HERO_IMAGE } from "../mock";

function Header() {
  return (
    <header className="sticky top-0 z-20 bg-[#141414]/95 backdrop-blur border-b border-[#2a2a2a]">
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="uppercase font-semibold tracking-[0.15em] text-lg" style={{ fontFamily: 'Oswald, sans-serif' }}>
          Corner Office <span className="text-[#e63946]">Curmudgeon</span>
        </Link>
        <Link to="/admin" className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-[#e63946] transition-colors">
          <Lock className="w-3.5 h-3.5" /> Admin
        </Link>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-[#2a2a2a] py-12 text-center">
      <h4 className="text-4xl font-bold uppercase text-[#e63946]" style={{ fontFamily: 'Oswald, sans-serif' }}>{brand.footerHeadline}</h4>
      <p className="text-gray-400 mt-2 italic">{brand.footerSub}</p>
      <p className="text-gray-600 text-sm mt-6">{brand.copyright}</p>
    </footer>
  );
}

export default function BlogHome() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, []);

  const ordered = [...posts].reverse();
  const featured = ordered[0];
  const rest = ordered.slice(1);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#141414", color: "#f5f5f5", fontFamily: 'Lora, Georgia, serif' }}>
      <Header />

      <section className="relative">
        <img src={HERO_IMAGE} alt={brand.name} className="w-full object-cover max-h-[420px]" />
        <div className="absolute inset-x-0 bottom-0 h-24" style={{ background: "linear-gradient(to top, #141414, transparent)" }} />
      </section>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#e63946]" /></div>
      ) : posts.length === 0 ? (
        <div className="max-w-3xl mx-auto px-6 py-24 text-center text-gray-400">No grumbles published yet.</div>
      ) : (
        <>
          {/* Featured */}
          <article className="max-w-5xl mx-auto px-6 py-14">
            <div className="grid md:grid-cols-[300px_1fr] gap-8 items-start">
              <Link to={`/post/${featured.id}`} className="group block overflow-hidden border border-[#2a2a2a]">
                <img src={thumbSrc(featured.thumbnail) || HERO_IMAGE} alt={featured.title} className="w-full h-56 md:h-64 object-cover group-hover:scale-105 transition-transform duration-300" />
              </Link>
              <div>
                <p className="uppercase tracking-[0.3em] text-[#e63946] text-xs mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>{featured.subtitle || "Latest"}</p>
                <Link to={`/post/${featured.id}`}>
                  <h1 className="text-3xl md:text-5xl font-bold uppercase leading-[1.05] mb-4 hover:text-[#e63946] transition-colors" style={{ fontFamily: 'Oswald, sans-serif' }}>{featured.title}</h1>
                </Link>
                <p className="text-gray-300 text-lg leading-relaxed">{featured.excerpt}</p>
                <Link to={`/post/${featured.id}`} className="inline-block mt-5 text-[#e63946] uppercase tracking-widest text-sm hover:underline" style={{ fontFamily: 'Oswald, sans-serif' }}>Read the grumble →</Link>
              </div>
            </div>
          </article>

          {rest.length > 0 && (
            <section className="max-w-5xl mx-auto px-6 pb-20">
              <h3 className="text-3xl font-bold uppercase mb-8 text-[#e63946]" style={{ fontFamily: 'Oswald, sans-serif' }}>Grumbles In The Vault</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {rest.map((post) => (
                  <Link to={`/post/${post.id}`} key={post.id} className="group flex gap-4 bg-[#1c1c1c] border border-[#2a2a2a] p-4 hover:border-[#e63946] transition-colors">
                    <img src={thumbSrc(post.thumbnail) || HERO_IMAGE} alt={post.title} className="w-28 h-28 object-cover transition-all flex-shrink-0" />
                    <div>
                      <h4 className="text-lg font-semibold uppercase leading-tight group-hover:text-[#e63946] transition-colors" style={{ fontFamily: 'Oswald, sans-serif' }}>{post.title}</h4>
                      <p className="text-gray-400 italic text-sm mt-1">{post.subtitle}</p>
                      <p className="text-gray-500 text-sm mt-2">{post.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <Footer />
    </div>
  );
}
